import { z } from "zod"

export const password = z
  .string()
  .min(8, "Must be at least 8 characters")
  .max(128, "Must be less than 128 characters")
  .refine(
    (val) =>
      /[A-Z]/.test(val) && // at least one uppercase letter
      /[0-9]/.test(val) && // at least one digit
      /[~!@#$%^&*_+\-=`|\\(){}\[\]:;"'<>,.?/]/.test(val), // at least one special character
    {
      message:
        "Password must include at least one uppercase letter, one number, and one special character",
    }
  )

export const LoginInput = z.object({
  email: z.string().email(),
  password: z.string(),
})
export type LoginInputType = z.infer<typeof LoginInput>

// Google identity is taken only from the verified ID token, never from
// separately supplied email/name fields.
export const GoogleLoginInput = z.object({
  credential: z.string().min(1),
})
export type GoogleLoginInputType = z.infer<typeof GoogleLoginInput>

export const ForgotPasswordInput = z.object({
  email: z.string().email(),
})
export type ForgotPasswordInputType = z.infer<typeof ForgotPasswordInput>

export const ResetPasswordInput = z
  .object({
    password: password,
    passwordConfirmation: password,
    token: z.string(),
  })
  .refine((data) => data.password === data.passwordConfirmation, {
    message: "Passwords don't match",
    path: ["passwordConfirmation"], // set path of error
  })
export type ResetPasswordInputType = z.infer<typeof ResetPasswordInput>

export const SignupInput = z.object({
  email: z.string().email(),
  password,
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  phone: z.string().optional(),
  address1: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  zipCode: z.string().optional(),
  smsOptIn: z.boolean().optional(),
  token: z.string().optional(),
})
export type SignupInputType = z.infer<typeof SignupInput>

export const ImpersonateUserInput = z.object({
  userId: z.number(),
})
export type ImpersonateUserInputType = z.infer<typeof ImpersonateUserInput>

export const InviteOrgInput = z.object({
  userId: z.number(),
  email: z.string().email(),
})
export type InviteOrgInputType = z.infer<typeof InviteOrgInput>

export const BannerNotificationInput = z.object({
  bannerMsg: z.string().optional().nullable(),
})
export type BannerNotificationInputType = z.infer<typeof BannerNotificationInput>

export const SmsJobCountInput = z.object({
  smsCount: z.string().optional().nullable(),
})
export type SmsJobCountInputType = z.infer<typeof SmsJobCountInput>

export const ProctorPasswordInput = z.object({
  newPassword: z
    .string()
    .min(8, "Must be at least 8 characters")
    .max(128, "Must be less than 128 characters")
    .refine(
      (val) =>
        /[A-Z]/.test(val) && // at least one uppercase letter
        /[a-z]/.test(val) && // at least one lowercase letter
        /[0-9]/.test(val) && // at least one digit
        /[~!@#$%^&*_+\-=`|\\(){}\[\]:;"'<>,.?/]/.test(val), // at least one special character
      {
        message:
          "Password must include at least one uppercase letter, one lowercase letter, one number, and one special character",
      }
    ),
})
